import { defaultApi, defaultClient } from '@mint-sdk/api-core';
import { z } from 'zod';

import type {
  CampaignManagerAdSchema,
  CampaignManagerCreativeSchema,
  TrackerMappingSchema,
} from '../shared/schemas/trackerMappings.js';
import {
  CreateAdformDisplayTrackerItemSchema,
  CreateAdformDisplayTrackersRequestSchema,
  CreateAdformDisplayTrackersResponseSchema,
  CreateAdformTrackingTrackerItemSchema,
  CreateAdformTrackingTrackersRequestSchema,
  CreateAdformTrackingTrackersResponseSchema,
  CreateCmDisplayTrackerItemSchema,
  CreateCmDisplayTrackersRequestSchema,
  CreateCmDisplayTrackersResponseSchema,
  CreateCmTrackingTrackerItemSchema,
  CreateCmTrackingTrackersRequestSchema,
  CreateCmTrackingTrackersResponseSchema,
  CreateTrackersRequestSchema,
  CreateTrackersTaskResponseSchema,
  DeleteTrackersQuerySchema,
  TrackersQuerySchema,
  TrackersResponseSchema,
  type TrackerSchema,
} from '../shared/schemas/trackers.js';

export const getTrackers = defaultClient.query({
  url: 'omni-trafficking/v1/external-data/trackers/',
  method: 'GET',
  responseSchema: TrackersResponseSchema,
  processResponse(data) {
    return data?.data?.items;
  },
  querySchema: TrackersQuerySchema,
  snakeCaseQueryParams: true,
});

export type TrackerItemType = z.infer<typeof TrackerSchema>;
export type TrackerType = z.infer<typeof TrackerSchema>;
export type TrackersResponseType = z.infer<typeof TrackersResponseSchema>;

export const createActivityTrackers = defaultClient.mutation({
  url: 'omni-trafficking/v1/tracker-mappings/' as const,
  method: 'POST',
  responseSchema: z.object({}),
  processResponse(data) {
    return data;
  },
  requestSchema: CreateTrackersRequestSchema,
  querySchema: z.object({
    settingsId: z.string().optional(),
  }),
  snakeCaseQueryParams: true,
});

export type CreateTrackersRequestType = z.infer<
  typeof CreateTrackersRequestSchema
>;

export const createCmTrackingTrackers = defaultApi.declareEndpoint({
  url: 'omni-trafficking/v1/tracker-mappings/campaign-manager/1x1/' as const,
  method: 'POST',
  responseSchema: CreateCmTrackingTrackersResponseSchema,
  requestSchema: CreateCmTrackingTrackersRequestSchema,
  querySchema: z.object({
    settingsId: z.string(),
  }),
  snakeCaseQueryParams: true,
});

export type CreateCmTrackingTrackerItemType = z.infer<
  typeof CreateCmTrackingTrackerItemSchema
>;
export type CreateCmTrackingTrackersResponseType = z.infer<
  typeof CreateCmTrackingTrackersResponseSchema
>;

export const createCmDisplayTrackers = defaultApi.declareEndpoint({
  url: 'omni-trafficking/v1/tracker-mappings/campaign-manager/display/' as const,
  method: 'POST',
  responseSchema: CreateCmDisplayTrackersResponseSchema,
  requestSchema: CreateCmDisplayTrackersRequestSchema,
  querySchema: z.object({
    settingsId: z.string(),
  }),
  snakeCaseQueryParams: true,
});

export type CreateCmDisplayTrackerItemType = z.infer<
  typeof CreateCmDisplayTrackerItemSchema
>;
export type CreateCmDisplayTrackersRequestType = z.infer<
  typeof CreateCmDisplayTrackersRequestSchema
>;
export type CreateCmDisplayTrackersResponseType = z.infer<
  typeof CreateCmDisplayTrackersResponseSchema
>;

export const createAdformTrackingTrackers = defaultApi.declareEndpoint({
  url: 'omni-trafficking/v1/tracker-mappings/adform/1x1/' as const,
  method: 'POST',
  responseSchema: CreateAdformTrackingTrackersResponseSchema,
  requestSchema: CreateAdformTrackingTrackersRequestSchema,
  querySchema: z.object({
    settingsId: z.string(),
  }),
  snakeCaseQueryParams: true,
});

export type CreateAdformTrackingTrackerItemType = z.infer<
  typeof CreateAdformTrackingTrackerItemSchema
>;
export type CreateAdformTrackingTrackersResponseType = z.infer<
  typeof CreateAdformTrackingTrackersResponseSchema
>;

export const createAdformDisplayTrackers = defaultApi.declareEndpoint({
  url: 'omni-trafficking/v1/tracker-mappings/adform/display/' as const,
  method: 'POST',
  responseSchema: CreateAdformDisplayTrackersResponseSchema,
  requestSchema: CreateAdformDisplayTrackersRequestSchema,
  querySchema: z.object({
    settingsId: z.string(),
  }),
  snakeCaseQueryParams: true,
});

export type CreateAdformDisplayTrackerItemType = z.infer<
  typeof CreateAdformDisplayTrackerItemSchema
>;
export type CreateAdformDisplayTrackersResponseType = z.infer<
  typeof CreateAdformDisplayTrackersResponseSchema
>;

export type CreateTrackersTaskResponseType = z.infer<
  typeof CreateTrackersTaskResponseSchema
>;

export type TrackerMappingSchemaType = z.infer<typeof TrackerMappingSchema>;
export type CampaignManagerAdSchemaType = z.infer<
  typeof CampaignManagerAdSchema
>;

export type CampaignManagerCreativeSchemaType = z.infer<
  typeof CampaignManagerCreativeSchema
>;

export const deleteActivityTrackers = defaultClient.mutation({
  url: 'omni-trafficking/v1/tracker-mappings/delete/' as const,
  method: 'DELETE',
  responseSchema: z.object({}),
  processResponse(data) {
    return data;
  },
  querySchema: DeleteTrackersQuerySchema,
  snakeCaseQueryParams: true,
});

export type DeleteTrackersQueryType = z.infer<typeof DeleteTrackersQuerySchema>;
